import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { qk } from '@/api/keys';
import { useAccount, type Area } from '@/app/session';
import { useTheme } from '@/app/theme';
import { Avatar, Button, ButtonLink, Drawer, Icon, Menu } from '@/components/ui';
import { money } from '@/lib/format';
import { ROLE_LABEL } from '@/lib/labels';
import { PortalMark } from '@/components/brand/PortalMark';
import s from './AppShell.module.css';

interface NavDef { to: string; label: string; icon: string; area?: Area; end?: boolean; count?: number }

function Sidebar({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  const { can, isStaff, accountId } = useAccount();
  const unread = useQuery({
    queryKey: qk.inbox(accountId, { unread: true }),
    queryFn: () => api.inbox(accountId, { unread: true }),
    enabled: can('leads'),
    refetchInterval: 60_000,
  });
  const unreadCount = unread.data?.length ?? 0;
  const groups: { label?: ReactNode; items: NavDef[] }[] = [
    { items: [{ to: '/', label: 'Dashboard', icon: 'home', end: true }] },
    {
      label: 'Sell',
      items: [
        { to: '/listings', label: 'Listings', icon: 'list', area: 'listings' },
        { to: '/leads', label: 'Leads', icon: 'inbox', area: 'leads', count: unreadCount },
      ],
    },
    {
      label: 'Grow',
      items: [
        { to: '/campaigns', label: 'Campaigns', icon: 'megaphone', area: 'campaigns' },
        { to: '/analytics', label: 'Analytics', icon: 'chart', area: 'analytics' },
        { to: '/storefront', label: 'Storefront', icon: 'store', area: 'storefront' },
      ],
    },
    {
      label: 'Account',
      items: [
        { to: '/billing', label: 'Billing', icon: 'wallet', area: 'billing' },
        { to: '/team', label: 'Team', icon: 'users', area: 'team' },
        { to: '/settings', label: 'Settings', icon: 'settings', area: 'settings' },
      ],
    },
  ];
  if (isStaff) groups.push({ label: <><span className="brand-name">LowOps</span> staff</>, items: [{ to: '/admin', label: 'Admin', icon: 'shield' }] });

  return (
    <nav className={[s.sidebar, className].filter(Boolean).join(' ')} aria-label="Main">
      <Link to="/" className={s.brand} onClick={onNavigate}>
        <PortalMark height={26} />
      </Link>
      {groups.map((g, gi) => {
        const items = g.items.filter((it) => !it.area || can(it.area));
        if (!items.length) return null;
        return (
          <div key={gi} className={s.navGroup}>
            {g.label ? <div className={s.navLabel}>{g.label}</div> : null}
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} className={s.navItem} onClick={onNavigate}>
                <Icon name={it.icon} size={18} />
                {it.label}
                {it.count ? <span className={s.navCount} aria-label={`${it.count} unread`}>{it.count}</span> : null}
              </NavLink>
            ))}
          </div>
        );
      })}
      <div className={s.sideFoot}>
        {api.mode === 'mock' ? <div className={s.mockBadge}><strong>Demo mode.</strong> Mock data — nothing is saved or charged.</div> : null}
        <a href={import.meta.env.VITE_SITE_URL || 'https://flylowops.com'} className="small muted" target="_blank" rel="noreferrer">
          flylowops.com marketplace <Icon name="external" size={12} style={{ verticalAlign: '-1px' }} />
        </a>
      </div>
    </nav>
  );
}

function AccountSwitcher() {
  const { account, accounts, switchAccount, role } = useAccount();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return (
    <Menu
      align="left"
      label="Switch account"
      trigger={(p) => (
        <button className={s.switcher} {...p} aria-label={`Account: ${account.display_name}. Switch account`}>
          <Avatar name={account.display_name} src={account.storefront?.logo_url} size={26} />
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, lineHeight: 1.2 }}>
            <span className={s.switcherName}>{account.display_name}</span>
            <span className={s.switcherRole}>{account.kind === 'dealer' ? 'Dealer' : 'Individual'} · {ROLE_LABEL[role].label}</span>
          </span>
          <Icon name="chevronDown" size={15} />
        </button>
      )}
      items={[
        { heading: 'Your accounts' },
        ...accounts.map((a) => ({
          label: <span>{a.display_name} <span className="muted small">· {a.kind === 'dealer' ? 'Dealer' : 'Individual'}</span></span>,
          icon: a.id === account.id ? 'check' : a.kind === 'dealer' ? 'store' : 'users',
          onSelect: () => { switchAccount(a.id); void qc.invalidateQueries({ queryKey: ['acct'] }); navigate('/'); },
        })),
        { separator: true },
        { label: 'Add a dealer account', icon: 'plus', to: '/onboarding?new=dealer' },
      ]}
    />
  );
}

function BalancePill() {
  const { accountId, can } = useAccount();
  const q = useQuery({ queryKey: qk.billing(accountId), queryFn: () => api.billingSummary(accountId), enabled: can('billing'), staleTime: 30_000 });
  if (!can('billing') || !q.data) return null;
  const total = q.data.paid_cents + q.data.promo_cents;
  return (
    <Link to="/billing" className={s.balance} title="Available balance (paid + promo credit)">
      <Icon name="wallet" size={15} />
      <strong className="num">{money(total)}</strong>
      {q.data.promo_cents > 0 ? <span className={s.balanceSplit}>incl. {money(q.data.promo_cents)} credit</span> : null}
    </Link>
  );
}

function UserMenu() {
  const { me } = useAccount();
  const { pref, setPref } = useTheme();
  const navigate = useNavigate();
  return (
    <Menu
      label="Your profile"
      trigger={(p) => (
        <button {...p} style={{ background: 'none', border: 0, padding: 2, cursor: 'pointer', borderRadius: '50%' }} aria-label="Profile and preferences">
          <Avatar name={me.display_name} size={32} />
        </button>
      )}
      items={[
        { heading: me.email },
        { label: 'Theme: system', icon: pref === 'system' ? 'check' : 'monitor', onSelect: () => setPref('system') },
        { label: 'Theme: light', icon: pref === 'light' ? 'check' : 'sun', onSelect: () => setPref('light') },
        { label: 'Theme: dark', icon: pref === 'dark' ? 'check' : 'moon', onSelect: () => setPref('dark') },
        { separator: true },
        { label: 'Sign out', icon: 'logout', onSelect: () => { void api.signOut().then(() => navigate('/login')); } },
      ]}
    />
  );
}

export function AppShell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { can } = useAccount();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <div className={s.shell}>
      <Sidebar className={s.desktop} />
      <div className={s.main}>
        <header className={s.topbar}>
          <Button className={s.menuBtn} variant="ghost" icon="menu" aria-label="Open menu" onClick={() => setOpen(true)} />
          <AccountSwitcher />
          <div className={s.topSpacer} />
          <BalancePill />
          {can('listings') ? <ButtonLink to="/listings/new" variant="primary" size="sm" icon="plus" className={s.hideSm}>New listing</ButtonLink> : null}
          <UserMenu />
        </header>
        <main id="main" className={[s.content, wide && s.wide].filter(Boolean).join(' ')}>{children}</main>
      </div>
      <Drawer open={open} onClose={() => setOpen(false)} side="left" label="Menu">
        <Sidebar className={s.drawerSidebar} onNavigate={() => setOpen(false)} />
      </Drawer>
    </div>
  );
}
